/*
 * Copyright (c) 2022 E-WIND - Thais Watanabe.
 * All rights reserved.
 */
import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";

import {
  queryInspectionResults,
  queryInspectionModels,
} from "src/services/AmplifyQuery";

import Layout from "src/components/layouts/Layout.react";
import LoadingCard from "src/components/loading/LoadingCard.react";
import Card from "src/components/cards/Card.react";
import {
  Table,
  ColHeader,
  TableBody,
  Col,
} from "src/components/tables/InspectionTable.react";

import { PrinterIcon } from "@heroicons/react/24/outline";

/**
 * The Special component
 *
 * @author Thais Watanabe
 */
const Special = () => {
  const location = useLocation();

  const { inspection, turbineInspections } = location.state;

  const [results, setResults] = useState(null);
  const [maintenance, setMaintenance] = useState(null);
  const [nonconformity, setNonconformity] = useState(null);
  const [followUp, setFollowUp] = useState(null);
  const [inspectionModels, setInspectionModels] = useState(null);

  /* useEffect */
  useEffect(() => {
    fetchInspectionResults();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * fetchInspectionResults
   */
  async function fetchInspectionResults() {
    // eslint-disable-next-line no-dupe-keys
    const filter = {
      or: [
        ...turbineInspections.map((obj) => ({
          turbineInspectionID: { eq: obj.id },
        })),
      ],
    };

    queryInspectionResults(filter, setResults);
  }

  /* useEffect */
  useEffect(() => {
    fetchInspectionModels();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * fetchInspectionModelItems
   */
  async function fetchInspectionModels() {
    queryInspectionModels(null, setInspectionModels);
  }

  /* useEffect */
  useEffect(() => {
    let seibigo = [];
    let futekigo = [];
    let keika = [];

    results?.forEach((result) => {
      result?.childrenResults?.forEach((child, index) => {
        const turbineInspection = turbineInspections.find(
          (obj) => obj.id === result.turbineInspectionID
        );

        const modelId = result?.itemId.split("-")[0];
        const model = inspectionModels?.find(
          (obj) => obj.modelNumber === +modelId
        );

        const obs = result?.childrenObs?.[index];
        const items = obs?.split("・").filter((item) => item);

        switch (child) {
          case "整備後適合":
            items?.forEach((obj) => {
              seibigo.push({
                itemId: `${result.itemId}-${index + 1}`,
                model: model?.name,
                result: child,
                obs: obj,
                tower: turbineInspection.windTurbine.tower,
              });
            });

            break;

          case "不適合":
            items?.forEach((obj) => {
              futekigo.push({
                itemId: `${result.itemId}-${index + 1}`,
                model: model?.name,
                result: child,
                obs: obj,
                tower: turbineInspection.windTurbine.tower,
              });
            });

            break;

          case "経過観察":
            items?.forEach((obj) => {
              keika.push({
                itemId: `${result.itemId}-${index + 1}`,
                model: model?.name,
                result: child,
                obs: obj,
                tower: turbineInspection.windTurbine.tower,
              });
            });

            break;

          default:
            break;
        }
      });
    });

    setMaintenance(seibigo);
    setNonconformity(futekigo);
    setFollowUp(keika);
  }, [inspectionModels, results, turbineInspections]);

  const PrintButton = () => {
    return (
      <button
        type="button"
        onClick={() => window.print()}
        className="ml-3 inline-flex items-center p-2 border border-transparent rounded-full shadow-sm text-white bg-teal-600 hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500"
      >
        {/* <!-- Heroicon name: printer --> */}
        <PrinterIcon className="h-6 w-6" />
      </button>
    );
  };

  return (
    <Layout
      title="特記事項"
      breadcrumbs={["登録済点検一覧", "登録済点検一覧（号機）", "結果"]}
      printButton={<PrintButton />}
    >
      <Card>
        <div className="bg-white px-4 py-5 border-b border-gray-200 sm:px-6">
          <div className="-ml-4 -mt-2 flex items-center justify-between flex-wrap sm:flex-nowrap">
            <div className="ml-4 mt-2">
              <h3 className="text-md leading-6 font-medium text-gray-900">
                {inspection.windFarm.name} ・ {inspection.name}
              </h3>
            </div>
          </div>
        </div>
      </Card>

      {!maintenance ? (
        <LoadingCard />
      ) : (
        <Card title="整備後適合" className="table-special">
          <Table>
            <thead>
              <tr>
                <ColHeader className="print:text-xxs">No</ColHeader>
                <ColHeader className="print:text-xxs">場所</ColHeader>
                <ColHeader className="print:text-xxs">内容</ColHeader>
                <ColHeader className="print:text-xxs">号機</ColHeader>
              </tr>
            </thead>
            <TableBody>
              {maintenance.map((obj, i) => (
                <tr key={i}>
                  <Col className="print:text-xxs">{i + 1}</Col>
                  <Col className="print:text-xxs">{obj.model}</Col>
                  <Col className="print:text-xxs">{obj.obs}</Col>
                  <Col className="print:text-xxs">{obj.tower}</Col>
                </tr>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      {!nonconformity ? (
        <LoadingCard />
      ) : (
        <Card title="不適合" className="table-special">
          <Table>
            <thead>
              <tr>
                <ColHeader className="print:text-xxs">No</ColHeader>
                <ColHeader className="print:text-xxs">場所</ColHeader>
                <ColHeader className="print:text-xxs">内容</ColHeader>
                <ColHeader className="print:text-xxs">号機</ColHeader>
              </tr>
            </thead>
            <TableBody>
              {nonconformity.map((obj, i) => (
                <tr key={i}>
                  <Col className="print:text-xxs">{i + 1}</Col>
                  <Col className="print:text-xxs">{obj.model}</Col>
                  <Col className="print:text-xxs">{obj.obs}</Col>
                  <Col className="print:text-xxs">{obj.tower}</Col>
                </tr>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      {!followUp ? (
        <LoadingCard />
      ) : (
        <Card title="経過観察" className="table-special">
          <Table>
            <thead>
              <tr>
                <ColHeader className="print:text-xxs">No</ColHeader>
                <ColHeader className="print:text-xxs">場所</ColHeader>
                <ColHeader className="print:text-xxs">内容</ColHeader>
                <ColHeader className="print:text-xxs">号機</ColHeader>
              </tr>
            </thead>
            <TableBody>
              {followUp.map((obj, i) => (
                <tr key={i}>
                  <Col className="print:text-xxs">{i + 1}</Col>
                  <Col className="print:text-xxs">{obj.model}</Col>
                  <Col className="print:text-xxs">{obj.obs}</Col>
                  <Col className="print:text-xxs">{obj.tower}</Col>
                </tr>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
    </Layout>
  );
};

export default Special;
